// Chemistry Evolution — ECharts option builders
// Builds options for: Ragone by Cathode, Cathode Share (Area),
// NMC Evolution (Stacked Bar), Ragone by Anode, Anode Share (Area).

import {
  J_TO_WH,
  TEXT_PRIMARY, TEXT_SECONDARY, TEXT_TERTIARY,
  FONT_BODY, FONT_TITLE, PALETTE,
  _rgba, assignColors, _scatterStyle,
  _baseTitle, _baseGrid, _baseTooltip, _baseAxis, _logYAxis, _baseLegend,
  _hullSeries, _hullSwappedSeries,
} from './radarChartTheme.js';

// ---------------------------------------------------------------------------
// NMC sub-type colour scheme (cool→warm gradient by nickel content)
// ---------------------------------------------------------------------------

const NMC_COLORS = {
  // Sub-types — cool→warm gradient by nickel content (blends merged into base)
  NMC111: '#93c5e3',            // Soft Sky Blue (lowest nickel)
  NMC532: '#12a0be',            // Bright Teal
  NMC622: '#f0b028',            // Bright Amber
  NMC721: '#e88c28',            // Warm Orange
  NMC811: '#ea5b0c',            // Brand Orange (high nickel)
  NMC95: '#9cc840',             // Olive Green (highest nickel)
  // Fallback group names
  'NMC (high-nickel)': '#ea5b0c',
  'NMC (low-nickel)': '#f0b028',
};

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function _assignColors(groups) {
  return assignColors(groups);
}


function _baseAxisStyle() {
  return {
    axisLabel: { color: TEXT_SECONDARY, fontFamily: FONT_BODY, fontSize: 12 },
    nameTextStyle: { color: TEXT_PRIMARY, fontFamily: FONT_BODY, fontSize: 13, padding: [8, 0, 0, 0] },
    axisLine: { lineStyle: { color: 'rgba(148,163,184,0.08)' } },
    splitLine: { lineStyle: { color: 'rgba(148,163,184,0.08)' } },
  };
}

// ---------------------------------------------------------------------------
// _buildRagoneScatter  (shared by cathode & anode Ragone plots)
// ---------------------------------------------------------------------------

function _buildRagoneScatter(points, hulls, basis, title) {
  const eKey = basis === 'vol' ? 'e_vol' : 'e_grav';
  const pKey = basis === 'vol' ? 'p_vol' : 'p_grav';

  const valid = points.filter((pt) => pt[eKey] != null && pt[pKey] != null && pt.group !== 'analysis ongoing');
  const groups = [...new Set(valid.map((pt) => pt.group))];
  const colorMap = _assignColors(groups);

  const scatterSeries = groups.map((group) => {
    const groupPts = valid.filter((pt) => pt.group === group);
    const color = colorMap.get(group);
    return {
      name: group,
      type: 'scatter',
      data: groupPts.map((pt) => ({
        value: [pt[pKey], pt[eKey] * J_TO_WH],
        _meta: pt,
      })),
      symbolSize: 7,
      itemStyle: _scatterStyle(color),
      emphasis: {
        focus: 'series',
        itemStyle: { opacity: 1, borderColor: '#fff', borderWidth: 2, shadowBlur: 6, shadowColor: 'rgba(0,0,0,0.4)' },
      },
      blur: {
        itemStyle: { opacity: 0.08 },
      },
    };
  });

  // Gradient hull series (swapped: [power, energy])
  const hullSeriesList = [];
  if (hulls) {
    for (const group of groups) {
      const hullData = hulls[group];
      if (!hullData) continue;
      const coords = hullData[basis];
      if (!coords || coords.length === 0) continue;

      const baseColor = colorMap.get(group);
      const hull = _hullSwappedSeries(group, coords, baseColor);
      if (hull) {
        hullSeriesList.push(hull);
      }
    }
  }

  const xLabel =
    basis === 'vol' ? 'Volumetric Peak Power Density (W/L)' : 'Gravimetric Peak Power Density (W/kg)';
  const yLabel =
    basis === 'vol' ? 'Volumetric Energy Density (Wh/L)' : 'Gravimetric Energy Density (Wh/kg)';

  return {
    backgroundColor: 'transparent',
    title: _baseTitle(title),
    tooltip: _baseTooltip({
      formatter(params) {
        const meta = params.data?._meta;
        if (!meta) return '';
        const p = params.value[0];
        const e = params.value[1];
        const eUnit = basis === 'vol' ? 'Wh/L' : 'Wh/kg';
        const pUnit = basis === 'vol' ? 'W/L' : 'W/kg';
        return [
          `<strong>${meta.manufacturer} ${meta.name}</strong>`,
          `${meta.group}`,
          `Energy: ${e.toFixed(1)} ${eUnit}`,
          `Power: ${p.toFixed(1)} ${pUnit}`,
        ].join('<br/>');
      },
    }),
    legend: _baseLegend(groups),
    grid: _baseGrid(),
    xAxis: _baseAxis(xLabel),
    yAxis: _baseAxis(yLabel, { nameGap: 55, nameRotate: 90 }),
    series: [...scatterSeries, ...hullSeriesList],
  };
}

// ---------------------------------------------------------------------------
// _buildStackedArea  (100% stacked area chart for share)
// ---------------------------------------------------------------------------

function _buildStackedArea(marketShare, title, groupOrder) {
  if (!marketShare) return {};

  const years = Object.keys(marketShare).sort();

  // Filter out "analysis ongoing" and re-normalize to 100% per year
  const normalized = {};
  years.forEach((y) => {
    if (!marketShare[y]) return;
    const filtered = {};
    let total = 0;
    Object.entries(marketShare[y]).forEach(([g, v]) => {
      if (g !== 'analysis ongoing') {
        filtered[g] = v;
        total += v;
      }
    });
    if (total > 0) {
      const scale = 100 / total;
      normalized[y] = {};
      Object.entries(filtered).forEach(([g, v]) => {
        normalized[y][g] = v * scale;
      });
    }
  });

  const groupSet = new Set();
  years.forEach((y) => {
    if (normalized[y]) Object.keys(normalized[y]).forEach((g) => groupSet.add(g));
  });

  let groups;
  if (groupOrder) {
    const ordered = groupOrder.filter((g) => groupSet.has(g));
    const rest = [...groupSet].filter((g) => !groupOrder.includes(g)).sort();
    groups = [...ordered, ...rest];
  } else {
    groups = [...groupSet].sort();
  }

  const colorMap = _assignColors(groups);

  const series = groups.map((group, idx) => ({
    name: group,
    type: 'line',
    stack: 'total',
    areaStyle: {
      opacity: 0.85,
    },
    emphasis: { focus: 'series' },
    symbol: 'none',
    lineStyle: { width: 1.5, color: colorMap.get(group) },
    itemStyle: { color: colorMap.get(group) },
    data: years.map((y) =>
      normalized[y] && normalized[y][group] != null ? normalized[y][group] : 0
    ),
    animationDelay: (idx) => idx * 80,
  }));

  return {
    backgroundColor: 'transparent',
    title: _baseTitle(title),
    tooltip: {
      ..._baseTooltip({ trigger: 'axis' }),
      axisPointer: { type: 'cross', label: { backgroundColor: '#333' } },
      formatter(params) {
        if (!params || params.length === 0) return '';
        const year = params[0].axisValue;
        const lines = [`<strong>${year}</strong>`];
        params.forEach((p) => {
          if (p.value > 0) {
            lines.push(
              `<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${p.color};margin-right:6px;"></span>${p.seriesName}: ${p.value.toFixed(1)}%`
            );
          }
        });
        return lines.join('<br/>');
      },
    },
    legend: _baseLegend(groups),
    grid: _baseGrid(),
    xAxis: _baseAxis('Year', { type: 'category', data: years }),
    yAxis: _baseAxis('Share (%)', {
      nameGap: 55,
      nameRotate: 90,
      min: 0,
      max: 100,
      axisLabel: {
        color: TEXT_TERTIARY,
        fontFamily: FONT_BODY,
        fontSize: 11,
        formatter: '{value}%',
      },
    }),
    series,
  };
}

// ---------------------------------------------------------------------------
// _buildStackedBar  (for NMC evolution — flat, clean bars)
// ---------------------------------------------------------------------------

function _buildStackedBar(marketShare, title, groupOrder, colorOverrides) {
  if (!marketShare) return {};

  const years = Object.keys(marketShare).sort();

  // Filter "analysis ongoing" and re-normalize to 100% per year
  const normalized = {};
  years.forEach((y) => {
    if (!marketShare[y]) return;
    const filtered = {};
    let total = 0;
    Object.entries(marketShare[y]).forEach(([g, v]) => {
      if (g !== 'analysis ongoing') {
        filtered[g] = v;
        total += v;
      }
    });
    if (total > 0) {
      const scale = 100 / total;
      normalized[y] = {};
      Object.entries(filtered).forEach(([g, v]) => {
        normalized[y][g] = v * scale;
      });
    }
  });

  const groupSet = new Set();
  years.forEach((y) => {
    if (normalized[y]) Object.keys(normalized[y]).forEach((g) => groupSet.add(g));
  });

  let groups;
  if (groupOrder) {
    const ordered = groupOrder.filter((g) => groupSet.has(g));
    const rest = [...groupSet].filter((g) => !groupOrder.includes(g)).sort();
    groups = [...ordered, ...rest];
  } else {
    groups = [...groupSet].sort();
  }

  const colorMap = colorOverrides
    ? new Map(Object.entries(colorOverrides))
    : _assignColors(groups);

  const series = groups.map((group) => ({
    name: group,
    type: 'bar',
    stack: 'total',
    barCategoryGap: '25%',
    data: years.map((y) =>
      normalized[y] && normalized[y][group] != null ? normalized[y][group] : 0
    ),
    itemStyle: {
      color: colorMap.get(group),
      borderRadius: [2, 2, 0, 0],
    },
    emphasis: { focus: 'series' },
    animationDelay: (idx) => idx * 150,
  }));

  return {
    backgroundColor: 'transparent',
    title: _baseTitle(title),
    tooltip: {
      ..._baseTooltip({ trigger: 'axis' }),
      axisPointer: { type: 'shadow' },
      formatter(params) {
        if (!params || params.length === 0) return '';
        const year = params[0].axisValue;
        const lines = [`<strong>${year}</strong>`];
        params.forEach((p) => {
          if (p.value > 0) {
            lines.push(
              `<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${p.color};margin-right:6px;"></span>${p.seriesName}: ${p.value.toFixed(1)}%`
            );
          }
        });
        return lines.join('<br/>');
      },
    },
    legend: _baseLegend(groups),
    grid: _baseGrid(),
    xAxis: _baseAxis('Year', { type: 'category', data: years }),
    yAxis: _baseAxis('Share (%)', {
      nameGap: 55,
      nameRotate: 90,
      min: 0,
      max: 100,
      axisLabel: {
        color: TEXT_TERTIARY,
        fontFamily: FONT_BODY,
        fontSize: 11,
        formatter: '{value}%',
      },
    }),
    series,
  };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function buildRagoneByCathode(data, basis = 'grav') {
  return _buildRagoneScatter(
    data.ragone_cathode || [],
    data.hulls_cathode,
    basis,
    'Ragone Plot — Cathode Chemistry'
  );
}

export function buildCathodeMarketShare(data) {
  return _buildStackedBar(data.market_share_cathode, 'Cathode Chemistry — Share');
}

export function buildNmcEvolution(data) {
  // Merge "blend" sub-types into their base category (e.g. "NMC532 - blend" → "NMC532")
  const raw = data.nmc_evolution;
  if (!raw) return {};
  const merged = {};
  Object.entries(raw).forEach(([year, groups]) => {
    merged[year] = {};
    Object.entries(groups).forEach(([key, value]) => {
      const base = key.replace(/\s*-?\s*[Bb]lend$/, '');
      merged[year][base] = (merged[year][base] || 0) + value;
    });
  });

  const nmcOrder = ['NMC111', 'NMC532', 'NMC622', 'NMC721', 'NMC811', 'NMC95'];
  return _buildStackedBar(
    merged,
    'NMC Evolution — Nickel Content Trend',
    nmcOrder,
    NMC_COLORS
  );
}

export function buildRagoneByAnode(data, basis = 'grav') {
  return _buildRagoneScatter(
    data.ragone_anode || [],
    data.hulls_anode,
    basis,
    'Ragone Plot — Anode Chemistry'
  );
}

export function buildAnodeMarketShare(data) {
  return _buildStackedBar(data.market_share_anode, 'Anode Chemistry — Share');
}
